import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SET_COURSE_COMPLETION } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useMyProgress } from "./useMyProgress";
import { useProductFeatures } from "./useProductFeatures";
import type { CourseCompletionProgress } from "../types/mmp-graphql";

// The signed-in climber's course completions, sourced from their per-product
// prg- record (courseProgress). Consumers read courseID/completedAt off each
// entry (CourseCompletionProgress fields are optional — the prg- shape).
export const useGetMyCourseCompletions = () => {
  const { progress, loading, isFetching, error, refetch } = useMyProgress();
  const data = useMemo<CourseCompletionProgress[]>(
    () => progress?.courseProgress ?? [],
    [progress],
  );
  return {
    data,
    loading,
    isLoading: loading,
    isFetching,
    isError: !!error,
    error,
    refetch,
  };
};

export const useCompleteCourse = () => {
  const queryClient = useQueryClient();
  const { activeProduct } = useProductFeatures();

  const mutation = useMutation({
    mutationFn: async ({ courseID }: { courseID: string }) => {
      const productCode = activeProduct?.code;
      if (!productCode) {
        throw new Error("No active product; cannot record course completion");
      }
      const { setCourseCompletion } = (await graphQLClient.request(
        SET_COURSE_COMPLETION,
        { input: { courseID, productCode } },
      )) as { setCourseCompletion: unknown };
      return setCourseCompletion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myProgress"] });
    },
  });

  return { completeCourse: mutation };
};
